import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AgentChat from "../../components/AgentChat/AgentChat";
import RulesContext from "../../context/rules-context";
import {
    buildCompanyEventPayload,
    collectRulePieceKeys,
    fetchRulePieceDefinitions
} from "./RuleTree/ruleAgentClient";
import { translateOutcomeTreeData } from "./RuleTree/treeUtils";

/* Everything the dash agent needs to know about rules is assembled here and handed to the
   generic AgentChat, which stays unaware of this domain. When another screen of the dashboard
   gains its own skill, it writes its own adapter beside this one — none of them touch the chat.

   The context texts are read by the model, not by the user, so they stay in Portuguese literals
   like the prompts in ruleAgentClient.js; translating them would change what the agent is told,
   not what anyone sees. */

function buildRuleEntry(ruleData) {
    let name = ruleData.name ? ' "' + ruleData.name + '"' : "";
    let text = ruleData.is_rule_piece
        ? "Estado atual do trecho de regra reutilizável" + name + " aberto no editor do usuário. " +
          "O campo tree_data traz a árvore completa do trecho."
        : "Estado atual da regra" + name + " aberta no editor do usuário. " +
          "O campo tree_data traz a árvore completa da regra.";
    return { text: text, data: ruleData };
}

function buildCompanyEventEntry(payload) {
    return {
        text:
            "Catálogo do que está disponível para montar regras nesta empresa e neste tipo de evento: " +
            "boxes, mapeamento de campos do evento, variáveis de processadores, sinais, scripts e listas.",
        data: payload
    };
}

function buildRulePieceEntry(definition) {
    return {
        text:
            'Definição do trecho de regra reutilizável "' + definition.name + '" (' +
            definition.rule_piece_key + "), referenciado pela árvore aberta no editor.",
        data: definition
    };
}

function RuleEngineAgentChat({ treeData, userVariablesAvailable, ruleName, agentContext }) {
    const { t } = useTranslation();
    const rulesContext = useContext(RulesContext);

    const isRulePiece = Boolean(rulesContext.isRulePiece);
    const environment = rulesContext.environment;
    const companyKey = rulesContext.company_key;

    /* Serialized so everything downstream keys off the CONTENT of the rule, not the identity of
       a freshly built object. That is what keeps an unchanged rule from being re-sent with every
       message — the same dedup the old client did with lastSentRuleJsonRef. */
    const ruleDataJson = useMemo(() => {
        let data = { tree_data: translateOutcomeTreeData(treeData) };
        if (userVariablesAvailable && userVariablesAvailable.length > 0) {
            data.user_variables = userVariablesAvailable;
        }
        if (ruleName) data.name = ruleName;
        if (isRulePiece) data.is_rule_piece = true;
        if (agentContext) data.agent_context = agentContext;
        return JSON.stringify(data);
    }, [treeData, userVariablesAvailable, ruleName, isRulePiece, agentContext]);

    const [rulePieceEntries, setRulePieceEntries] = useState([]);

    useEffect(() => {
        let keys = collectRulePieceKeys(JSON.parse(ruleDataJson).tree_data);
        if (keys.length === 0) {
            setRulePieceEntries([]);
            return undefined;
        }
        let cancelled = false;
        fetchRulePieceDefinitions({ keys: keys, environment: environment, companyKey: companyKey })
            .then((definitions) => {
                if (cancelled) return;
                setRulePieceEntries(definitions.map(buildRulePieceEntry));
            });
        return () => {
            cancelled = true;
        };
    }, [ruleDataJson, environment, companyKey]);

    const turnContext = useMemo(
        () => [buildRuleEntry(JSON.parse(ruleDataJson))].concat(rulePieceEntries),
        [ruleDataJson, rulePieceEntries]
    );

    const chatContext = useMemo(() => {
        let payload = buildCompanyEventPayload(rulesContext);
        return payload ? [buildCompanyEventEntry(payload)] : [];
    }, [rulesContext]);

    const isEmptyTree = !treeData || treeData.length === 0;
    const suggestions = useMemo(
        () =>
            isEmptyTree
                ? [
                    t("Sugira validações comuns para este tipo de evento"),
                    t("Monte uma estrutura inicial para esta regra"),
                    t("Quais campos do evento posso usar?"),
                    t("Quais dados externos posso consultar?")
                ]
                : [
                    t("O que esta regra faz?"),
                    t("Explique a última alteração"),
                    t("Há algum problema nesta regra?"),
                    t("Como simplificar esta regra?")
                ],
        [isEmptyTree, t]
    );

    return (
        <AgentChat
            chatContext={chatContext}
            turnContext={turnContext}
            title={t("Assistente de Regras")}
            greeting={t("Olá! Como posso ajudar com esta regra?")}
            emptySubtitle={
                isEmptyTree
                    ? t("Peça sugestões ou gere validações para começar a montar sua regra.")
                    : t("Tire dúvidas ou peça análises sobre a regra que você está editando.")
            }
            suggestions={suggestions}
        />
    );
}

export default RuleEngineAgentChat;
